// src/lib/config.ts
import logger from './logger';

// Always use the environment variable for API URL
// This allows easy switching between local, preview, and production backends
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
export const TELEMETRY_DISABLED = import.meta.env.VITE_DISABLE_TELEMETRY === 'true';

// Log the API URL being used
logger.log('API URL:', API_BASE_URL);

// Validate the API URL
if (!API_BASE_URL) {
  throw new Error('VITE_API_URL is not set. Check your environment variables.');
}

// Ensure no trailing slash
const normalizedApiUrl = API_BASE_URL.replace(/\/$/, '');

export const API_CONFIG = {
  baseURL: normalizedApiUrl,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

// Sentry Configuration
const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

// Determine environment name
const getEnvironment = (): string => {
  const configuredEnvironment = import.meta.env.VITE_APP_ENVIRONMENT?.trim();
  if (configuredEnvironment) return configuredEnvironment;
  if (isDevelopment) return 'development';
  if (normalizedApiUrl.includes('staging')) return 'staging';
  return 'production';
};

const sentryEnvironment = getEnvironment();
const numberFromEnv = (value: string | undefined, fallback: number): number => {
  const parsed = value === undefined ? Number.NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getApiOrigin = (): string => {
  try {
    return new URL(normalizedApiUrl).origin;
  } catch {
    return normalizedApiUrl;
  }
};

export const SENTRY_CONFIG = {
  dsn: sentryDsn,
  environment: sentryEnvironment,
  release: import.meta.env.VITE_APP_VERSION || 'dev',
  enabled: Boolean(sentryDsn) && isProduction && !TELEMETRY_DISABLED,
  tracePropagationTargets: [getApiOrigin()],
  // Production defaults to 10%; staging/development default to complete traces.
  tracesSampleRate: numberFromEnv(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    sentryEnvironment === 'production' ? 0.1 : 1.0
  ),
  profilesSampleRate: numberFromEnv(
    import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE,
    sentryEnvironment === 'production' ? 0.1 : 0
  ),
  replaysSessionSampleRate: numberFromEnv(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    sentryEnvironment === 'production' ? 0.1 : 0
  ),
  replaysOnErrorSampleRate: numberFromEnv(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    1.0
  ),
  captureHandledApiErrors: import.meta.env.VITE_SENTRY_CAPTURE_HANDLED_API_ERRORS === 'true',
  captureWebVitalIssues: import.meta.env.VITE_SENTRY_CAPTURE_WEB_VITAL_ISSUES === 'true',
};
